/* eslint-disable react/jsx-key */
import React from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button, Col, List, Row } from 'antd-v5';
import { SettingsContent } from '../../../components/SettingsContent';
import { getActionConfig } from '../../../../../../core/apiRequests';
import {
  setReminderAddEdit,
  setReminderActionId,
  setReminderInterval,
  setReminderCount,
  setReminderRepeat,
  setReminderName,
  setReminderActive,
  setRemRepeatCount,
  setRemRepeatInterval,
  setExpireScale,
  setExpireInterval,
  setExpires,
  setAutomationTarget,
} from '../../../../../../reducers/autoRemMsgs';

const ROLEX_INQUIRY_TYPE = 'rolexInquiry';
const ROLEX_INQUIRY_LIST_ITEM_ID = 'default';

const formatTiming = (scale, interval) => {
  if (scale === undefined || scale === null) return '\u2014';
  if (scale === 0 && interval === 'DAY') return 'Same Day';
  if (!interval) return '\u2014';
  const unit = interval.charAt(0) + interval.slice(1).toLowerCase();
  return `${scale} ${unit}${scale > 1 ? 's' : ''}`;
};

const InquiryRemindersComponent = ({ merchantId }) => {
  const { push } = useHistory();

  const { data: reminders, isFetching } = useQuery(
    ['inquiryAutoReminders', merchantId],
    async () => {
      if (!merchantId) return [];
      const data = await getActionConfig(
        ROLEX_INQUIRY_TYPE,
        merchantId,
        ROLEX_INQUIRY_LIST_ITEM_ID,
        'automationLists',
      );
      return (data || []).filter(item => item.type === 'Auto-Reminder');
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
      enabled: !!merchantId,
    },
  );

  const handleAddReminder = () => {
    setReminderAddEdit('Add');
    setReminderActionId('');
    setReminderName('');
    setReminderInterval('WEEK');
    setReminderCount(2);
    setReminderRepeat('');
    setReminderActive(1);
    push('/Configuration/rolex/add-auto-reminder');
  };

  const handleEditReminder = item => {
    const {
      id,
      description,
      action_interval,
      action_scale,
      repeats,
      active,
      repeat_scale,
      repeat_interval,
      expire_interval,
      expire_scale,
      automation_target_cd,
    } = item;
    setReminderAddEdit('Edit');
    setReminderActionId(id);
    setReminderName(description);
    setReminderActive(active);
    setReminderInterval(action_interval);
    setReminderCount(action_scale);
    setReminderRepeat(repeats);
    setRemRepeatCount(repeat_scale);
    setRemRepeatInterval(repeat_interval);
    if (expire_interval || expire_scale) {
      setExpireScale(expire_scale);
      setExpireInterval(expire_interval);
      setExpires(true);
    } else {
      setExpires(false);
    }
    setAutomationTarget(automation_target_cd || '');
    push(`/Configuration/rolex/edit-auto-reminder/${id}`);
  };

  return (
    <SettingsContent
      title="Reminders"
      description="Configure auto reminders for inquiry follow-ups"
      action={
        <Button type="primary" onClick={handleAddReminder}>
          Add Auto Reminder
        </Button>
      }
    >
      <List
        header={<ReminderConfigHeader />}
        className="w-100-P m-btm-16"
        size="small"
        dataSource={reminders}
        renderItem={item => <ReminderConfigItem item={item} onEdit={handleEditReminder} />}
        loading={isFetching}
        locale={{ emptyText: 'No reminders configured' }}
      />
    </SettingsContent>
  );
};

const ReminderConfigHeader = () => (
  <Row justify="space-between" align="middle" style={{ width: '100%' }}>
    <Col span={5}>
      <HeaderItem name="Name" />
    </Col>
    <Col span={4}>
      <HeaderItem name="Timing" />
    </Col>
    <Col span={5}>
      <HeaderItem name="Assigned To" />
    </Col>
    <Col span={2}>
      <HeaderItem name="Repeats" />
    </Col>
    <Col span={2}>
      <HeaderItem name="Status" />
    </Col>
    <Col span={2} />
  </Row>
);

const HeaderItem = ({ name }) => (
  <div className="flex-row p-left-6" style={{ color: '#0000006B' }}>
    {name}
  </div>
);

const ContentItem = ({ content }) => <div className="flex-row">{content}</div>;

const formatAssignment = automationTargetCd => {
  if (!automationTargetCd) return '\u2014';
  if (automationTargetCd === 'inquiry') return 'Inquiry Associate';
  if (automationTargetCd === 'assignment') return "Client's Associate";
  return automationTargetCd;
};

const ReminderConfigItem = ({ item, onEdit }) => (
  <List.Item style={{ margin: 0 }} styles={{ actions: { margin: 0 } }} key={item.id}>
    <Row justify="space-between" align="middle" style={{ width: '100%' }}>
      <Col span={5}>
        <ContentItem content={item.description} />
      </Col>
      <Col span={4}>
        <ContentItem content={formatTiming(item.action_scale, item.action_interval)} />
      </Col>
      <Col span={5}>
        <ContentItem content={formatAssignment(item.automation_target_cd)} />
      </Col>
      <Col span={2}>
        <ContentItem content={item.repeats ? 'Yes' : 'No'} />
      </Col>
      <Col span={2}>
        <ContentItem
          content={
            <span style={{ color: item.active === 1 ? '#7fbd31' : undefined }}>
              {item.active === 1 ? 'Enabled' : 'Disabled'}
            </span>
          }
        />
      </Col>
      <Col span={2}>
        <div className="flex-row items-end w-100-P justify-end">
          <Button onClick={() => onEdit(item)} variant="text" color="primary" htmlType="button" className="m-right-8">
            Edit
          </Button>
        </div>
      </Col>
    </Row>
  </List.Item>
);

const mapStateToProps = state => ({
  merchantId: state.auth.merchantId,
});

export const InquiryReminders = connect(mapStateToProps)(InquiryRemindersComponent);
